import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {Website} from "../../../website";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {BoosterMode} from "../../services/booster.service";
import {UserSubscription, UserSubscriptionType} from "../../../../../user/user-subscription";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {ActivityStatus, IActivity} from "../../../../../activities/activity";
import {Config} from "../../../../../config/config";
import {combineLatest} from "rxjs";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";

@Component({
    selector: 'db-booster-general',
    templateUrl: './booster-general.component.html',
    styleUrls: ['./booster-general.component.scss'],
    standalone: false
})
export class BoosterGeneralComponent implements OnInit, OnDestroy {

  @Input() companyName: any;
  @Input() boosterModeLoading: boolean = true;
  @Input() enabledCdnCount: number;
  public readonly userSubscriptionType = UserSubscriptionType;

  public testMode: 'on'|'off'|null = null;
  public testModeEnabled: boolean;
  public testModeLoading: boolean = true;
  public boosterMode: BoosterMode;
  public website: Website;
  public scoreDate: Date;
  public cacheEnabled: boolean;
  public cachingStatus: boolean;
  public userSubscription: UserSubscription;
  public trialBoosterWithHostedWebsite: boolean;
  public updateAvailable: boolean;
  public cdnIsActive: boolean;
  canEnable: boolean = false;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _route: ActivatedRoute,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _store: Store<fromStore.DbStoreState>,
  ) {}

  ngOnInit() {
    const websiteId = this._route.snapshot.parent.parent.data['website'].id;
    this._initData();
    this._getWebsite(websiteId);
    this._getBoosterMode(websiteId);
    this._getCacheEnabledStatus(websiteId);
    this._getScoreOngoingActivity();
    this._getTestMode();
    this.checkBoosterPluginUpdate(websiteId);
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _getScoreOngoingActivity(): void {
    this._store.select(fromStore.getGoogleScoreOngoingActivity(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((websiteScore: IActivity): void => {
        if (websiteScore && websiteScore.status === ActivityStatus.DONE) {
          this.scoreDate = websiteScore.date;
        }
      });
  }

  private _getBoosterMode(websiteId): void {
    this._store.select(fromStore.getWebsiteBoosterModeLevel(websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(stats => {
        if (!TypeUtils.isNull(stats)) {
          this.boosterMode = stats;
        }
      });
  }

  private _getTestMode(): void {
    this._store.select(fromStore.getWebsiteBoosterTestMode(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(mode => {
        if (!TypeUtils.isNull(mode)) {
          this.testMode = TypeUtils.isDefined(mode) ? mode : null;
          this.testMode = TypeUtils.isDefined(mode) && (mode === 'on' || mode === 'off') ? mode : null;
          this.testModeEnabled = !TypeUtils.isNull(this.testMode) && this.testMode === 'on';
          this.testModeLoading = false;
        }
      });
  }

  private _getCacheEnabledStatus(websiteId): void {
    this._store.select(fromStore.getWebsiteBoosterCacheStats(websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(cacheStats => {
        if (!TypeUtils.isNull(cacheStats)) {
          this.cachingStatus = true;
          this.cacheEnabled = TypeUtils.isDefined(cacheStats.cacheEnabled) ? cacheStats.cacheEnabled : false;
        }
      });
  }

  private _initData(): void {
    this.userSubscription = this._route.snapshot.parent.parent.data['userSubscription'];
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter(loaded => loaded),
        switchMap(() =>
          this._store.select(fromStore.getUserSubscription)
        ))
      .subscribe((userSubscription) => {
        this.userSubscription = userSubscription;
      });
  }

  private _getWebsite(websiteId): void {
    this._store.select(fromStore.getWebsite(websiteId))
      .pipe(
        filter(website => !!website),
        takeUntil(this._destroyStream$)
      )
      .subscribe(website => {
        this.website = website;
        this.trialBoosterWithHostedWebsite = this.userSubscription && this.userSubscription.trialBoosterClient && !!this.website.domain_hash;
        this.cdnIsActive = website.cloudflareEnabled;
        if (this.userSubscription) {
          if (!this.userSubscription.boosterProClient && this.userSubscription.plan && this.userSubscription.plan.additionalCDNCount) {
            this.canEnable = this.enabledCdnCount < (this.userSubscription.plan.additionalCDNCount + +(this.userSubscription.plan.cdnCountValue || 0))
              && this.website && !this.website.cloudflareEnabled;
          } else {
            this.canEnable = this.userSubscription && this.userSubscription.canEnableCdn && this.website && !this.website.cloudflareEnabled;
          }
        }
      });
  }

  public checkBoosterPluginUpdate(websiteId: number): void {
    this._store.select(fromStore.getWebsitePluginsLoaded(websiteId))
      .pipe(
        first(loaded => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteOptimizerPlugin(websiteId)),
          this._store.select(fromStore.getTenWebProduct(Config.optimizerPluginSlug))
        )),
        first()
      )
      .subscribe(([websitePlugin, tenWebPlugin]) => {
        // websitePlugin && websitePlugin.currentVersion && websitePlugin.currentVersion.paid;
      });
  }
}
